import { useState, useEffect, useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Menu, Button, Dropdown, Badge } from "antd";
import {
  DashboardOutlined,
  CloudUploadOutlined,
  SettingOutlined,
  DownloadOutlined,
  GiftOutlined,
  UserOutlined,
  LogoutOutlined,
  SunOutlined,
  MoonOutlined,
  HomeOutlined,
  UploadOutlined,
  TeamOutlined,
  IdcardOutlined,
  CrownOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { useUnreadAnnouncements } from "@/hooks/useAnnouncements";

type MenuItem = Required<MenuProps>["items"][number];

/** 不含公告的基础菜单项，公告项在组件内动态添加 */
const baseMenuItems: MenuItem[] = [
  { key: "/", icon: <DashboardOutlined />, label: "总览" },
  { key: "/resolve", icon: <CloudUploadOutlined />, label: "依赖查询" },
  { key: "/upload", icon: <UploadOutlined />, label: "上传查询" },
  { key: "/sources", icon: <SettingOutlined />, label: "自定义源" },
  { key: "/downloads", icon: <DownloadOutlined />, label: "我的下载" },
  { key: "/redeem", icon: <GiftOutlined />, label: "福利兑换" },
  { key: "/invitation", icon: <TeamOutlined />, label: "邀请好友" },
  { key: "/profile", icon: <IdcardOutlined />, label: "个人资料" },
  { key: "/membership", icon: <CrownOutlined />, label: "会员记录" },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const { user, loading: authLoading, logout } = useAuth();

  const { unreadCount } = useUnreadAnnouncements(!!user);

  const menuItems = useMemo<MenuItem[]>(
    () => [
      ...baseMenuItems,
      {
        key: "/announcements",
        icon: <NotificationOutlined />,
        label: (
          <Badge count={unreadCount} size="small" offset={[10, 0]} color="red">
            <span>站内公告</span>
          </Badge>
        ),
      },
    ],
    [unreadCount],
  );

  const activeKey = (() => {
    const path = location.pathname;
    if (path === "/") return "/";
    return "/" + path.split("/").filter(Boolean)[0];
  })();

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    navigate(key);
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: "profile",
      label: user?.username,
      icon: <UserOutlined />,
      disabled: true,
    },
    { key: "my-profile", label: "个人资料", icon: <IdcardOutlined /> },
    { key: "my-membership", label: "会员记录", icon: <CrownOutlined /> },
    { type: "divider" },
    { key: "logout", label: "退出登录", icon: <LogoutOutlined /> },
  ];

  const handleUserMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "logout") {
      logout();
      navigate("/login");
    } else if (key === "my-profile") {
      navigate("/profile");
    } else if (key === "my-membership") {
      navigate("/membership");
    }
  };

  const publicPaths = [
    "/",
    "/login",
    "/register",
    "/resolve",
    "/site-announcements",
  ];
  const isPublicPath = publicPaths.includes(location.pathname);

  useEffect(() => {
    if (!authLoading && !user && !isPublicPath) {
      navigate("/login");
    }
  }, [user, authLoading, location.pathname, navigate, isPublicPath]);

  if (!user) {
    return (
      <div className="min-h-screen">
        <header className="glass-nav relative z-50 h-16 flex items-center justify-between border-b border-white/20 bg-white/70 px-4 md:px-6 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="glass-btn-secondary group flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:from-blue-500/20 hover:to-purple-500/20 hover:shadow-md hover:shadow-blue-500/10 dark:text-white/90"
              title="返回首页"
            >
              <HomeOutlined className="text-base text-blue-500 transition-transform duration-200 group-hover:-translate-y-0.5" />
              <span>首页</span>
            </button>
            <div className="flex items-center gap-2 text-lg font-bold text-slate-800 dark:text-white">
              <CloudUploadOutlined className="text-blue-500" />
              <span>GLPD 公共查询</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="text"
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              className="glass-btn-secondary text-slate-700 dark:text-white/90"
            >
              {isDark ? "亮色" : "暗色"}
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => navigate("/login")}
              className="glass-btn-pill border-transparent bg-blue-500/80 text-white"
            >
              登录
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="glass-nav relative z-50 h-16 flex items-center justify-between border-b border-white/20 bg-white/70 px-4 md:px-6 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="glass-btn-secondary group flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:from-blue-500/20 hover:to-purple-500/20 hover:shadow-md hover:shadow-blue-500/10 dark:text-white/90"
            title="返回首页"
          >
            <HomeOutlined className="text-base text-blue-500 transition-transform duration-200 group-hover:-translate-y-0.5" />
            <span>首页</span>
          </button>
          <div className="flex items-center gap-2 text-lg font-bold text-slate-800 dark:text-white">
            <CloudUploadOutlined className="text-blue-500" />
            <span>GLPD 公共查询</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="text"
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
            className="glass-btn-secondary text-slate-700 dark:text-white/90"
          >
            {isDark ? "亮色" : "暗色"}
          </Button>
          <Dropdown
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<UserOutlined />}
              className="glass-btn-secondary text-slate-700 dark:text-white/90"
            >
              {user.username}
            </Button>
          </Dropdown>
        </div>
      </header>

      <div className="flex min-h-screen pt-16">
        <aside
          className={`glass-surface sticky top-16 hidden shrink-0 flex-col p-3 md:flex ${collapsed ? "w-[72px]" : "w-[220px]"}`}
          style={{ height: "calc(100vh - 4rem)" }}
        >
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={[activeKey]}
            items={menuItems}
            onClick={onMenuClick}
            className="flex-1 border-r-0 bg-transparent"
          />
          <Button
            type="text"
            size="small"
            onClick={() => setCollapsed((c) => !c)}
            className="glass-btn-secondary mt-2 text-slate-600 dark:text-white/80"
          >
            {collapsed ? "展开" : "收起"}
          </Button>
        </aside>

        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
